// TODO: This is temporary file, should be removed when TS migration is done.
import { Component, PureComponent } from 'react';
import { arrayOf } from 'prop-types';
import isPlainObject from 'prop-types-exact/build/helpers/isPlainObject';

function wrapValidator(validator, typeName, typeChecker = null) {
  return Object.assign(validator.bind(), {
    typeName,
    typeChecker,
    isRequired: Object.assign(validator.isRequired.bind(), {
      typeName,
      typeChecker,
      typeRequired: true,
    }),
  });
}

function oneOfTypeValidator(validators) {
  const validator = function oneOfType(
    props,
    propName,
    componentName,
    ...rest
  ) {
    const { [propName]: propValue } = props;

    if (typeof propValue === 'undefined') {
      return null;
    }

    const errors = validators
      .map(v => v(props, propName, componentName, ...rest))
      .filter(Boolean);

    if (errors.length < validators.length) {
      return null;
    }

    return new TypeError(
      `${componentName}: invalid value supplied to ${propName}.`,
    );
  };

  validator.isRequired = function oneOfTypeRequired(
    props,
    propName,
    componentName,
    ...rest
  ) {
    const { [propName]: propValue } = props;

    if (typeof propValue === 'undefined') {
      return new TypeError(
        `${componentName}: missing value for required ${propName}.`,
      );
    }

    const errors = validators
      .map(v => v(props, propName, componentName, ...rest))
      .filter(Boolean);

    if (errors.length === validators.length) {
      return new TypeError(
        `${componentName}: invalid value ${errors} supplied to required ${propName}.`,
      );
    }

    return null;
  };

  return wrapValidator(validator, 'oneOfType', validators);
}

const or = (validators, name = 'or') => {
  if (!Array.isArray(validators)) {
    throw new TypeError('or: 2 or more validators are required');
  }

  if (validators.length <= 1) {
    throw new RangeError('or: 2 or more validators are required');
  }

  const validator = oneOfTypeValidator([
    arrayOf(oneOfTypeValidator(validators)),
    ...validators,
  ]);

  return wrapValidator(validator, name, validators);
};

function explicitNull(props, propName, componentName) {
  if (props[propName] == null) {
    return null;
  }

  return new TypeError(
    `${componentName}: prop “${propName}” must be null or undefined; received ${typeof props[propName]}`,
  );
}
explicitNull.isRequired = function explicitNullRequired(
  props,
  propName,
  componentName,
) {
  if (props[propName] === null) {
    return null;
  }

  return new TypeError(
    `${componentName}: prop “${propName}” must be null; received ${typeof props[propName]}`,
  );
};

const explicitNullWrapper = () => wrapValidator(explicitNull, 'explicitNull');

const andValidator = (validators, name = 'and') => {
  if (!Array.isArray(validators)) {
    throw new TypeError('and: 2 or more validators are required');
  }

  if (validators.length <= 1) {
    throw new RangeError('and: 2 or more validators are required');
  }

  const validator = function and(...args) {
    let firstError = null;

    validators.some(validatorFn => {
      firstError = validatorFn(...args);

      return firstError != null;
    });

    return firstError == null ? null : firstError;
  };

  validator.isRequired = function andIsRequired(...args) {
    let firstError = null;

    validators.some(validatorFn => {
      firstError = validatorFn.isRequired(...args);

      return firstError != null;
    });

    return firstError == null ? null : firstError;
  };

  return wrapValidator(validator, name, validators);
};

const shapeValidator = shapeTypes => {
  if (!isPlainObject(shapeTypes)) {
    throw new TypeError('shape must be a normal object');
  }

  function shape(props, propName, componentName, location, ...rest) {
    const { [propName]: propValue } = props;

    if (propValue == null) {
      return null;
    }
    // code adapted from PropTypes.shape: https://github.com/facebook/react/blob/14156e56b9cf18ac86963185c5af4abddf3ff811/src/isomorphic/classic/types/ReactPropTypes.js#L381
    // eslint-disable-next-line guard-for-in, no-restricted-syntax

    for (const key in shapeTypes) {
      const checker = shapeTypes[key];

      if (checker) {
        const error = checker(propValue, key, componentName, location, ...rest);

        if (error) {
          return error;
        }
      }
    }

    return null;
  }

  shape.isRequired = function shapeRequired(
    props,
    propName,
    componentName,
    ...rest
  ) {
    const { [propName]: propValue } = props;

    if (propValue == null) {
      return new TypeError(`${componentName}: ${propName} is required.`);
    }

    return shape(props, propName, componentName, ...rest);
  };

  return wrapValidator(shape, 'shape', shapeTypes);
};

const { isPrototypeOf } = Object.prototype;

function isNewRef(prop) {
  if (!isPlainObject(prop)) {
    return false;
  }
  const ownProperties = Object.keys(prop);

  return ownProperties.length === 1 && ownProperties[0] === 'current';
}

function isCallbackRef(prop) {
  return (
    typeof prop === 'function' &&
    !isPrototypeOf.call(Component, prop) &&
    (!PureComponent || !isPrototypeOf.call(PureComponent, prop))
  );
}

function requiredRef(props, propName, componentName) {
  const propValue = props[propName];

  if (isCallbackRef(propValue) || isNewRef(propValue)) {
    return null;
  }

  return new TypeError(`${propName} in ${componentName} must be a ref`);
}

function ref(props, propName, componentName, ...rest) {
  const propValue = props[propName];

  if (propValue == null) {
    return null;
  }

  return requiredRef(props, propName, componentName, ...rest);
}

ref.isRequired = requiredRef;

const refValidator = () => wrapValidator(ref, 'ref');

export {
  refValidator as ref,
  shapeValidator as shape,
  or,
  andValidator as and,
  explicitNullWrapper as explicitNull,
};
